// src/components/outline/ProjectOutlinePage.jsx
import { AppSelect } from '../ui/AppSelect';
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { BookOpen, Users, MapPin, Clock, Upload, Save, Edit2, Check, X, ArrowLeft, Loader2 } from "lucide-react";
import { authenticatedFetch } from "../../utils/api";
import { API_HOST } from "../../utils/constants";
import { useTheme } from "../../context/ThemeContext";
import { useToast } from "../../context/ToastContext";
import { useStyle } from "../../context/StyleContext";
import { Button } from "../ui/Button";
import useProjectPermissions from "../../hooks/useProjectPermissions";
import { runTaskToCompletion } from "../../services/taskService";

const ProjectOutlinePage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();
  const { can } = useProjectPermissions(projectId);
  const { currentStyle } = useStyle();

  const [activeTab, setActiveTab] = useState("synopsis");
  const [outline, setOutline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [showImporter, setShowImporter] = useState(false);
  const [characters, setCharacters] = useState([]);
  const [scenes, setScenes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [costumeAssets, setCostumeAssets] = useState(null); // 服化道 Agent 结果 { characters, scenes, props }

  useEffect(() => {
    loadOutline();
  }, [projectId]);

  useEffect(() => {
    if (outline?.characterIndex) {
      try { setCharacters(JSON.parse(outline.characterIndex)); } catch { setCharacters([]); }
    }
    if (outline?.sceneIndex) {
      try { setScenes(JSON.parse(outline.sceneIndex)); } catch { setScenes([]); }
    }
    // 解析服化道 Agent 结果（存在 timeline 字段）
    if (outline?.timeline) {
      try {
        const costumeData = JSON.parse(outline.timeline);
        setCostumeAssets(costumeData);
      } catch { setCostumeAssets(null); }
    }
  }, [outline]);

  const handleUpdate = useCallback(async (type, idx, newData) => {
    let updatedCharacters = characters;
    let updatedScenes = scenes;
    let updatedOutlineJson = outline?.outlineJson;

    if (type === 'characters') {
      updatedCharacters = characters.map((c, i) => i === idx ? newData : c);
      setCharacters(updatedCharacters);
    } else if (type === 'scenes') {
      updatedScenes = scenes.map((s, i) => i === idx ? newData : s);
      setScenes(updatedScenes);
    } else if (type === 'outlineJson') {
      // newData 是完整的 parsed outline 对象
      updatedOutlineJson = JSON.stringify(newData);
      setOutline(prev => ({ ...prev, outlineJson: updatedOutlineJson }));
    }

    try {
      setSaving(true);
      await authenticatedFetch(`${API_HOST}/api/outline/${projectId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          characterIndex: JSON.stringify(updatedCharacters),
          sceneIndex: JSON.stringify(updatedScenes),
          outlineJson: updatedOutlineJson,
        }),
      });
      toast.success("已保存");
    } catch (e) {
      toast.error("保存失败: " + e.message);
    } finally {
      setSaving(false);
    }
  }, [characters, scenes, outline, projectId, toast]);

  const loadOutline = async () => {
    try {
      setLoading(true);
      const data = await authenticatedFetch(`${API_HOST}/api/outline/${projectId}`);
      // authenticatedFetch 已经自动解包 ApiResult，直接就是 ProjectOutline 对象
      setOutline(data);
    } catch (e) {
      // 404 会抛异常，捕获后设为 null
      console.error(e);
      setOutline(null);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (script) => {
    try {
      setGenerating(true);
      const provider = localStorage.getItem("ai_provider") || "wangsugem";
      const { result } = await runTaskToCompletion(
        "OUTLINE_GENERATE",
        { projectId: Number(projectId), script, provider, styleAnchor: currentStyle },
        {
          projectId: Number(projectId),
          provider,
          styleAnchor: currentStyle,
          source: "project_outline",
          timeoutMs: 30 * 60 * 1000,
        }
      );

      // authenticatedFetch 已经自动解包，result 直接就是 ProjectOutline 对象
      toast.success("大纲生成成功！");
      setOutline(result);
      setShowImporter(false);
    } catch (e) {
      toast.error("生成失败: " + e.message);
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
        <Loader2 className="animate-spin" size={32} style={{ color: theme.colors.accentPrimary }} />
      </div>
    );
  }

  if (!outline && !showImporter) {
    return (
      <div className="w-full h-full flex flex-col" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
        {/* Header */}
        <div className="h-14 border-b flex items-center justify-between px-6 shrink-0" style={{ borderColor: 'var(--color-border-primary)' }}>
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(`/project/${projectId}`)}
              className="p-1.5 rounded-lg transition-all duration-200"
              style={{ color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-tertiary)' }}
            >
              <ArrowLeft size={16} />
            </button>
            <h1 className="font-bold text-lg flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
              <BookOpen size={18} style={{ color: theme.colors.purplePrimary }} />
              剧情大纲
            </h1>
          </div>
        </div>

        {/* Empty State */}
        <div className="flex-1 flex flex-col items-center justify-center p-8">
          <BookOpen size={64} className="mb-4 opacity-20" style={{ color: 'var(--color-text-muted)' }} />
          <h2 className="text-xl font-bold mb-2" style={{ color: 'var(--color-text-primary)' }}>
            尚未创建剧情大纲
          </h2>
          <p className="text-sm mb-6" style={{ color: 'var(--color-text-muted)' }}>
            导入完整剧本，AI 将自动生成剧情大纲、人物索引和场景索引
          </p>
          <Button
            onClick={() => setShowImporter(true)}
            className="flex items-center gap-2"
            style={{ background: `linear-gradient(to right, ${theme.colors.purplePrimary}, ${theme.colors.bluePrimary})` }}
            disabled={!can('OUTLINE_GENERATE')}
          >
            <Upload size={16} />
            导入剧本
          </Button>
        </div>
      </div>
    );
  }

  if (showImporter) {
    return <OutlineImporter onGenerate={handleGenerate} onCancel={() => setShowImporter(false)} generating={generating} />;
  }

  let parsedOutline = {};
  try {
    if (outline?.outlineJson) parsedOutline = JSON.parse(outline.outlineJson);
  } catch {
    // outlineJson 不是合法 JSON（如内容审核拦截返回的纯文本）
  }

  return (
    <div className="w-full h-full flex flex-col" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      {/* Header */}
      <div className="h-14 border-b flex items-center justify-between px-6 shrink-0" style={{ borderColor: 'var(--color-border-primary)' }}>
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/project/${projectId}`)}
            className="p-1.5 rounded-lg transition-all duration-200"
            style={{ color: 'var(--color-text-muted)', backgroundColor: 'var(--color-bg-tertiary)' }}
          >
            <ArrowLeft size={16} />
          </button>
          <h1 className="font-bold text-lg flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <BookOpen size={18} style={{ color: theme.colors.purplePrimary }} />
            {parsedOutline.title || "剧情大纲"}
          </h1>
          {saving && <Loader2 size={14} className="animate-spin" style={{ color: 'var(--color-text-muted)' }} />}
        </div>
        <div className="flex items-center gap-2">
          {can('OUTLINE_GENERATE') && (
          <Button
            onClick={() => setShowImporter(true)}
            variant="outline"
            size="sm"
            className="flex items-center gap-2"
          >
            <Upload size={14} />
            重新导入
          </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b flex items-center px-6 gap-1 shrink-0" style={{ borderColor: 'var(--color-border-primary)' }}>
        <TabButton active={activeTab === "synopsis"} onClick={() => setActiveTab("synopsis")} icon={<BookOpen size={16} />} label="剧情大纲" />
        <TabButton active={activeTab === "characters"} onClick={() => setActiveTab("characters")} icon={<Users size={16} />} label={`人物索引 (${characters.length})`} />
        <TabButton active={activeTab === "scenes"} onClick={() => setActiveTab("scenes")} icon={<MapPin size={16} />} label={`场景索引 (${scenes.length})`} />
      </div>

      {/* Content */}
      <div key={activeTab} className="flex-1 overflow-y-auto custom-scrollbar p-6 animate-in fade-in duration-200">
        {activeTab === "synopsis" && <SynopsisPanel outline={parsedOutline} onUpdate={handleUpdate} />}
        {activeTab === "characters" && <CharacterIndexPanel characters={characters} projectId={projectId} onUpdate={handleUpdate} costumeAssets={costumeAssets} />}
        {activeTab === "scenes" && <SceneIndexPanel scenes={scenes} projectId={projectId} onUpdate={handleUpdate} costumeAssets={costumeAssets} />}
      </div>
    </div>
  );
};

// Tab Button Component
const TabButton = ({ active, onClick, icon, label }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-4 py-3 font-medium text-sm transition-all border-b-2"
      style={{
        color: active ? theme.colors.accentPrimary : 'var(--color-text-muted)',
        borderColor: active ? theme.colors.accentPrimary : 'transparent',
      }}
    >
      {icon}
      {label}
    </button>
  );
};

// Synopsis Panel - 支持编辑（简介 + 幕结构）
const SynopsisPanel = ({ outline, onUpdate }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const [editingSynopsis, setEditingSynopsis] = useState(false);
  const [synopsisText, setSynopsisText] = useState(outline.synopsis || '');
  const [editingActIdx, setEditingActIdx] = useState(null);
  const [editActData, setEditActData] = useState({});

  const handleSaveSynopsis = () => {
    const updated = { ...outline, synopsis: synopsisText };
    onUpdate('outlineJson', null, updated);
    setEditingSynopsis(false);
  };

  const handleEditAct = (idx) => {
    setEditingActIdx(idx);
    const act = outline.acts[idx];
    setEditActData({
      ...act,
      _keyEventsInput: (act.key_events || []).join('\n'),
    });
  };

  const handleSaveAct = (idx) => {
    const key_events = editActData._keyEventsInput
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean);
    const updatedAct = { ...editActData, key_events, _keyEventsInput: undefined };
    const updatedActs = outline.acts.map((a, i) => i === idx ? updatedAct : a);
    onUpdate('outlineJson', null, { ...outline, acts: updatedActs });
    setEditingActIdx(null);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* 简介 - 可编辑 */}
      {outline.synopsis !== undefined && (
        <div className="p-6 rounded-xl" style={{ backgroundColor: 'var(--color-bg-secondary)', border: `1px solid ${editingSynopsis ? theme.colors.accentPrimary : 'var(--color-border-primary)'}` }}>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-bold uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
              作品简介
            </h3>
            {!editingSynopsis && (
              <Button size="sm" variant="ghost" onClick={() => setEditingSynopsis(true)} className="flex items-center gap-1">
                <Edit2 size={12} /> 编辑
              </Button>
            )}
          </div>
          {editingSynopsis ? (
            <div className="space-y-3">
              <textarea
                rows={3}
                className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none"
                style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                value={synopsisText}
                onChange={e => setSynopsisText(e.target.value)}
              />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => { setSynopsisText(outline.synopsis); setEditingSynopsis(false); }}>取消</Button>
                <Button size="sm" onClick={handleSaveSynopsis} style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}>
                  <Check size={12} className="mr-1" /> 保存
                </Button>
              </div>
            </div>
          ) : (
            <p className="text-base leading-relaxed" style={{ color: 'var(--color-text-primary)' }}>
              {outline.synopsis}
            </p>
          )}
        </div>
      )}

      {/* 主视角 */}
      {outline.pov && (
        <div className="p-6 rounded-xl" style={{ backgroundColor: 'var(--color-bg-secondary)', border: '1px solid var(--color-border-primary)' }}>
          <h3 className="text-sm font-bold mb-3 uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
            主视角分析
          </h3>
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>角色:</span>
              <span className="text-base font-bold" style={{ color: theme.colors.accentPrimary }}>{outline.pov.character}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium" style={{ color: 'var(--color-text-muted)' }}>类型:</span>
              <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{outline.pov.type}</span>
            </div>
            {outline.pov.reason && (
              <div className="mt-2 pt-2 border-t" style={{ borderColor: 'var(--color-border-primary)' }}>
                <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{outline.pov.reason}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 幕结构 - 可编辑 */}
      {outline.acts && outline.acts.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-bold" style={{ color: 'var(--color-text-primary)' }}>剧情结构</h2>
          {outline.acts.map((act, idx) => {
            const isEditing = editingActIdx === idx;
            return (
              <div
                key={idx}
                className="p-6 rounded-xl space-y-3"
                style={{ backgroundColor: 'var(--color-bg-secondary)', border: `1px solid ${isEditing ? theme.colors.accentPrimary : 'var(--color-border-primary)'}` }}
              >
                {isEditing ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-bold mb-1 block" style={{ color: 'var(--color-text-muted)' }}>幕标题</label>
                        <input
                          className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                          style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                          value={editActData.title || ''}
                          onChange={e => setEditActData(p => ({ ...p, title: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold mb-1 block" style={{ color: 'var(--color-text-muted)' }}>章节范围</label>
                        <input
                          className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                          style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                          value={editActData.chapters || ''}
                          onChange={e => setEditActData(p => ({ ...p, chapters: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-bold mb-1 block" style={{ color: 'var(--color-text-muted)' }}>核心事件（每行一条）</label>
                      <textarea
                        rows={4}
                        className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none"
                        style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                        value={editActData._keyEventsInput || ''}
                        onChange={e => setEditActData(p => ({ ...p, _keyEventsInput: e.target.value }))}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs font-bold mb-1 block" style={{ color: 'var(--color-text-muted)' }}>关键转折</label>
                        <input
                          className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                          style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                          value={editActData.turning_point || ''}
                          onChange={e => setEditActData(p => ({ ...p, turning_point: e.target.value }))}
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold mb-1 block" style={{ color: 'var(--color-text-muted)' }}>情感基调</label>
                        <input
                          className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                          style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                          value={editActData.tone || ''}
                          onChange={e => setEditActData(p => ({ ...p, tone: e.target.value }))}
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button size="sm" variant="ghost" onClick={() => setEditingActIdx(null)}>取消</Button>
                      <Button size="sm" onClick={() => handleSaveAct(idx)} style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}>
                        <Check size={12} className="mr-1" /> 保存
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm" style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}>
                          {act.act}
                        </div>
                        <div>
                          <h3 className="font-bold text-base" style={{ color: 'var(--color-text-primary)' }}>{act.title}</h3>
                          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{act.chapters}</p>
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => handleEditAct(idx)} className="flex items-center gap-1 shrink-0">
                        <Edit2 size={12} /> 编辑
                      </Button>
                    </div>

                    {act.key_events && act.key_events.length > 0 && (
                      <div>
                        <p className="text-xs font-bold mb-2" style={{ color: 'var(--color-text-muted)' }}>核心事件</p>
                        <ul className="space-y-1">
                          {act.key_events.map((event, i) => (
                            <li key={i} className="text-sm flex items-start gap-2" style={{ color: 'var(--color-text-secondary)' }}>
                              <span style={{ color: theme.colors.accentPrimary }}>•</span>
                              {event}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {act.turning_point && (
                      <div className="pt-3 border-t" style={{ borderColor: 'var(--color-border-primary)' }}>
                        <p className="text-xs font-bold mb-1" style={{ color: 'var(--color-text-muted)' }}>关键转折</p>
                        <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{act.turning_point}</p>
                      </div>
                    )}

                    {act.tone && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>情感基调:</span>
                        <span className="text-xs font-medium" style={{ color: theme.colors.purplePrimary }}>{act.tone}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Character Index Panel - 支持人工校对
const CharacterIndexPanel = ({ characters, projectId, onUpdate, costumeAssets }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();
  const [editingIdx, setEditingIdx] = useState(null);
  const [editData, setEditData] = useState({});
  const [convertedIds, setConvertedIds] = useState(new Set());

  const handleEdit = (idx, char) => {
    setEditingIdx(idx);
    setEditData({ ...char, aliases: [...(char.aliases || [])] });
  };

  const handleSave = (idx) => {
    onUpdate('characters', idx, editData);
    setEditingIdx(null);
  };

  const handleCancel = () => setEditingIdx(null);

  const handleAliasChange = (val) => {
    // 逗号分隔输入转数组
    setEditData(prev => ({ ...prev, _aliasInput: val }));
  };

  const handleAliasBlur = (val) => {
    const aliases = val.split(/[,，]/).map(s => s.trim()).filter(Boolean);
    setEditData(prev => ({ ...prev, aliases, _aliasInput: undefined }));
  };

  const handleConvertToAsset = async (char, idx) => {
    try {
      // 优先使用服化道 Agent 生成的 imagePrompt
      let description = null;
      if (costumeAssets?.characters) {
        const costumeChar = costumeAssets.characters.find(c => c.name === char.name);
        if (costumeChar?.imagePrompt) {
          description = costumeChar.imagePrompt;
        }
      }
      // fallback: 拼接大纲自己的描述
      if (!description) {
        description = [
          char.appearance,
          char.hairstyle && `发型：${char.hairstyle}`,
          char.makeup_style && `妆容：${char.makeup_style}`,
          char.default_attire && `服饰：${char.default_attire}`,
          char.accessories && char.accessories !== '无' && `配饰：${char.accessories}`,
          char.visual_archetype && `风格：${char.visual_archetype}`,
          char.personality,
        ].filter(Boolean).join("。");
      }

      await authenticatedFetch(`${API_HOST}/api/characters`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          projectId,
          name: char.name,
          description,
        }),
      });
      setConvertedIds(prev => new Set([...prev, idx]));
      toast.success(`已将 ${char.name} 添加到资产库`);
      return true;
    } catch (e) {
      toast.error("添加失败: " + e.message);
      return false;
    }
  };

  // 一键导入所有角色到资产库
  const handleConvertAllToAssets = async () => {
    const unconverted = characters
      .map((char, idx) => ({ char, idx }))
      .filter(({ idx }) => !convertedIds.has(idx));

    if (unconverted.length === 0) {
      toast.info("所有角色已导入");
      return;
    }

    let successCount = 0;
    for (const { char, idx } of unconverted) {
      const ok = await handleConvertToAsset(char, idx);
      if (ok) {
        successCount++;
      }
    }
    if (successCount > 0) {
      toast.success(`已导入 ${successCount} 个角色到资产库`);
    }
  };

  const roleColor = (role) => {
    if (role === "主角") return { bg: `${theme.colors.accentPrimary}20`, text: theme.colors.accentPrimary };
    if (role === "反派") return { bg: `${theme.colors.error}20`, text: theme.colors.error };
    return { bg: `${theme.colors.bluePrimary}20`, text: theme.colors.bluePrimary };
  };

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      {/* 一键导入全部按钮 */}
      {characters.length > 0 && (
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={handleConvertAllToAssets}
            disabled={convertedIds.size >= characters.length}
            className="flex items-center gap-1.5"
            style={{
              backgroundColor: convertedIds.size >= characters.length ? 'var(--color-bg-tertiary)' : theme.colors.accentPrimary,
              color: convertedIds.size >= characters.length ? 'var(--color-text-muted)' : 'white',
            }}
          >
            <Users size={13} />
            {convertedIds.size >= characters.length ? '全部已导入' : `一键导入全部角色 (${characters.length - convertedIds.size})`}
          </Button>
        </div>
      )}
      {characters.map((char, idx) => {
        const isEditing = editingIdx === idx;
        const converted = convertedIds.has(idx);
        const rc = roleColor(char.role);

        return (
          <div key={idx} className="p-6 rounded-xl" style={{ backgroundColor: 'var(--color-bg-secondary)', border: `1px solid ${isEditing ? theme.colors.accentPrimary : 'var(--color-border-primary)'}` }}>
            {isEditing ? (
              /* 编辑模式 */
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold mb-1 block" style={{ color: 'var(--color-text-muted)' }}>姓名</label>
                    <input
                      className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                      style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                      value={editData.name || ''}
                      onChange={e => setEditData(p => ({ ...p, name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold mb-1 block" style={{ color: 'var(--color-text-muted)' }}>角色定位</label>
                    <AppSelect
                      className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                      style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                      value={editData.role || '配角'}
                      onChange={e => setEditData(p => ({ ...p, role: e.target.value }))}
                    >
                      {['主角', '配角', '反派', '路人'].map(r => <option key={r} value={r}>{r}</option>)}
                    </AppSelect>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold mb-1 block" style={{ color: 'var(--color-text-muted)' }}>
                    别名（逗号分隔，越全越好）
                  </label>
                  <input
                    className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                    style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                    value={editData._aliasInput !== undefined ? editData._aliasInput : (editData.aliases || []).join(', ')}
                    onChange={e => handleAliasChange(e.target.value)}
                    onBlur={e => handleAliasBlur(e.target.value)}
                    placeholder="小李, 阿明, 他, 那个男孩..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold mb-1 block" style={{ color: 'var(--color-text-muted)' }}>性别</label>
                    <AppSelect
                      className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                      style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                      value={editData.gender || '未知'}
                      onChange={e => setEditData(p => ({ ...p, gender: e.target.value }))}
                    >
                      {['男', '女', '未知'].map(g => <option key={g} value={g}>{g}</option>)}
                    </AppSelect>
                  </div>
                  <div>
                    <label className="text-xs font-bold mb-1 block" style={{ color: 'var(--color-text-muted)' }}>年龄</label>
                    <input
                      className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                      style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                      value={editData.age || ''}
                      onChange={e => setEditData(p => ({ ...p, age: e.target.value }))}
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold mb-1 block" style={{ color: 'var(--color-text-muted)' }}>外貌描述（用于生成定妆照）</label>
                  <textarea
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none"
                    style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                    value={editData.appearance || ''}
                    onChange={e => setEditData(p => ({ ...p, appearance: e.target.value }))}
                  />
                </div>

                <div>
                  <label className="text-xs font-bold mb-1 block" style={{ color: 'var(--color-text-muted)' }}>性格特点</label>
                  <textarea
                    rows={2}
                    className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none"
                    style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                    value={editData.personality || ''}
                    onChange={e => setEditData(p => ({ ...p, personality: e.target.value }))}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button size="sm" variant="ghost" onClick={handleCancel}>取消</Button>
                  <Button size="sm" onClick={() => handleSave(idx)} style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}>
                    <Check size={12} className="mr-1" /> 保存
                  </Button>
                </div>
              </div>
            ) : (
              /* 展示模式 */
              <>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-bold flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                      {char.name}
                      {char.role && (
                        <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: rc.bg, color: rc.text }}>
                          {char.role}
                        </span>
                      )}
                      {char.gender && <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{char.gender}</span>}
                      {char.age && <span className="text-xs" style={{ color: 'var(--color-text-muted)' }}>{char.age}</span>}
                    </h3>

                    {/* 别名 - 重点展示 */}
                    {char.aliases && char.aliases.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        <span className="text-xs font-bold" style={{ color: 'var(--color-text-muted)' }}>别名:</span>
                        {char.aliases.map((alias, i) => (
                          <span key={i} className="text-xs px-2 py-0.5 rounded-md" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}>
                            {alias}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2 shrink-0 ml-4">
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(idx, char)} className="flex items-center gap-1">
                      <Edit2 size={12} /> 编辑
                    </Button>
                    <Button
                      size="sm"
                      variant={converted ? "ghost" : "outline"}
                      onClick={() => !converted && handleConvertToAsset(char, idx)}
                      className="flex items-center gap-1"
                      disabled={converted}
                    >
                      <Check size={12} />
                      {converted ? '已添加' : '转为资产'}
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  {char.appearance && (
                    <div>
                      <p className="text-xs font-bold mb-1" style={{ color: 'var(--color-text-muted)' }}>外貌</p>
                      <p style={{ color: 'var(--color-text-secondary)' }}>{char.appearance}</p>
                    </div>
                  )}
                  {char.personality && (
                    <div>
                      <p className="text-xs font-bold mb-1" style={{ color: 'var(--color-text-muted)' }}>性格</p>
                      <p style={{ color: 'var(--color-text-secondary)' }}>{char.personality}</p>
                    </div>
                  )}
                </div>

                {char.arc && (
                  <div className="mt-3">
                    <p className="text-xs font-bold mb-1" style={{ color: 'var(--color-text-muted)' }}>人物弧线</p>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{char.arc}</p>
                  </div>
                )}

                {char.relationships && char.relationships.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-bold mb-2" style={{ color: 'var(--color-text-muted)' }}>关系网</p>
                    <div className="flex flex-wrap gap-2">
                      {char.relationships.map((rel, i) => (
                        <span key={i} className="text-xs px-2 py-1 rounded-lg" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}>
                          {rel.name} · {rel.relation}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );
      })}

    </div>
  );
};

// Scene Index Panel - 支持人工校对 + 转为资产
const SceneIndexPanel = ({ scenes, projectId, onUpdate, costumeAssets }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();
  const [editingIdx, setEditingIdx] = useState(null);
  const [editData, setEditData] = useState({});
  const [convertedIds, setConvertedIds] = useState(new Set());

  const handleEdit = (idx, scene) => {
    setEditingIdx(idx);
    setEditData({ ...scene });
  };

  const handleSave = (idx) => {
    onUpdate('scenes', idx, editData);
    setEditingIdx(null);
  };

  const handleConvertToAsset = async (scene, idx) => {
    try {
      // 优先使用服化道 Agent 生成的 imagePrompt
      let description = scene.description || '';
      if (costumeAssets?.scenes) {
        const costumeScene = costumeAssets.scenes.find(s => s.name === scene.name);
        if (costumeScene?.imagePrompt) {
          description = costumeScene.imagePrompt;
        }
      }

      await authenticatedFetch(`${API_HOST}/api/scenes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          projectId,
          name: scene.name,
          description,
          type: scene.type || '室内',
        }),
      });
      setConvertedIds(prev => new Set([...prev, idx]));
      toast.success(`已将 ${scene.name} 添加到场景库`);
      return true;
    } catch (e) {
      toast.error("添加失败: " + e.message);
      return false;
    }
  };

  // 一键导入所有场景到资产库
  const handleConvertAllToAssets = async () => {
    const unconverted = scenes
      .map((scene, idx) => ({ scene, idx }))
      .filter(({ idx }) => !convertedIds.has(idx));

    if (unconverted.length === 0) {
      toast.info("所有场景已导入");
      return;
    }
    let successCount = 0;
    for (const { scene, idx } of unconverted) {
      const ok = await handleConvertToAsset(scene, idx);
      if (ok) {
        successCount++;
      }
    }
    if (successCount > 0) {
      toast.success(`已导入 ${successCount} 个场景到资产库`);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-4">
      {/* 一键导入全部场景 */}
      {scenes.length > 0 && (
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={handleConvertAllToAssets}
            disabled={convertedIds.size >= scenes.length}
            className="flex items-center gap-1.5"
            style={{
              backgroundColor: convertedIds.size >= scenes.length ? 'var(--color-bg-tertiary)' : theme.colors.accentPrimary,
              color: convertedIds.size >= scenes.length ? 'var(--color-text-muted)' : 'white',
            }}
          >
            <MapPin size={13} />
            {convertedIds.size >= scenes.length ? '全部已导入' : `一键导入全部场景 (${scenes.length - convertedIds.size})`}
          </Button>
        </div>
      )}
      {scenes.map((scene, idx) => {
        const isEditing = editingIdx === idx;
        const converted = convertedIds.has(idx);
        return (
          <div key={idx} className="p-6 rounded-xl" style={{ backgroundColor: 'var(--color-bg-secondary)', border: `1px solid ${isEditing ? theme.colors.accentPrimary : 'var(--color-border-primary)'}` }}>
            {isEditing ? (
              <div className="space-y-3 animate-in fade-in duration-200">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold mb-1 block" style={{ color: 'var(--color-text-muted)' }}>场景名称</label>
                    <input
                      className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                      style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                      value={editData.name || ''}
                      onChange={e => setEditData(p => ({ ...p, name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold mb-1 block" style={{ color: 'var(--color-text-muted)' }}>类型</label>
                    <AppSelect
                      className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                      style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                      value={editData.type || '室内'}
                      onChange={e => setEditData(p => ({ ...p, type: e.target.value }))}
                    >
                      {['室内', '室外', '虚拟'].map(t => <option key={t} value={t}>{t}</option>)}
                    </AppSelect>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold mb-1 block" style={{ color: 'var(--color-text-muted)' }}>场景描述</label>
                  <textarea
                    rows={3}
                    className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none"
                    style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
                    value={editData.description || ''}
                    onChange={e => setEditData(p => ({ ...p, description: e.target.value }))}
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button size="sm" variant="ghost" onClick={() => setEditingIdx(null)}>取消</Button>
                  <Button size="sm" onClick={() => handleSave(idx)} style={{ backgroundColor: theme.colors.accentPrimary, color: 'white' }}>
                    <Check size={12} className="mr-1" /> 保存
                  </Button>
                </div>
              </div>
            ) : (
              <div className="animate-in fade-in duration-200">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="text-lg font-bold flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                      {scene.name}
                      {scene.type && (
                        <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}>
                          {scene.type}
                        </span>
                      )}
                    </h3>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-4">
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(idx, scene)} className="flex items-center gap-1">
                      <Edit2 size={12} /> 编辑
                    </Button>
                    <Button
                      size="sm"
                      variant={converted ? "ghost" : "outline"}
                      onClick={() => !converted && handleConvertToAsset(scene, idx)}
                      className="flex items-center gap-1"
                      disabled={converted}
                    >
                      <Check size={12} />
                      {converted ? '已添加' : '转为资产'}
                    </Button>
                  </div>
                </div>
                {scene.description && (
                  <p className="text-sm mb-3" style={{ color: 'var(--color-text-secondary)' }}>{scene.description}</p>
                )}
                {scene.chapters && scene.chapters.length > 0 && (
                  <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    <Clock size={12} />
                    出现章节: {scene.chapters.join(", ")}
                  </div>
                )}
                {scene.key_events && scene.key_events.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs font-bold mb-1" style={{ color: 'var(--color-text-muted)' }}>重要事件</p>
                    <div className="flex flex-wrap gap-1">
                      {scene.key_events.map((ev, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 rounded-md" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}>
                          {ev}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

    </div>
  );
};

// Outline Importer Component
const OutlineImporter = ({ onGenerate, onCancel, generating }) => {
  const [script, setScript] = useState("");
  const { getTheme } = useTheme();
  const theme = getTheme();

  return (
    <div className="w-full h-full flex flex-col" style={{ backgroundColor: 'var(--color-bg-primary)' }}>
      <div className="h-14 border-b flex items-center justify-between px-6 shrink-0" style={{ borderColor: 'var(--color-border-primary)' }}>
        <h1 className="font-bold text-lg" style={{ color: 'var(--color-text-primary)' }}>
          导入剧本
        </h1>
        <button
          onClick={onCancel}
          disabled={generating}
          className="p-2 rounded-lg transition-all duration-200"
          style={{ color: 'var(--color-text-muted)' }}
        >
          <X size={18} />
        </button>
      </div>

      <div className="flex-1 p-6 overflow-y-auto">
        <div className="max-w-4xl mx-auto space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>
              粘贴完整剧本或小说
            </label>
            <textarea
              value={script}
              onChange={(e) => setScript(e.target.value)}
              placeholder="粘贴完整的剧本或小说内容..."
              className="w-full h-96 p-4 rounded-xl resize-none outline-none font-mono text-sm"
              style={{
                backgroundColor: 'var(--color-bg-secondary)',
                border: '1px solid var(--color-border-primary)',
                color: 'var(--color-text-primary)',
              }}
              disabled={generating}
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button onClick={onCancel} variant="outline" disabled={generating}>
              取消
            </Button>
            <Button
              onClick={() => onGenerate(script)}
              disabled={!script.trim() || generating}
              className="flex items-center gap-2"
              style={{ background: `linear-gradient(to right, ${theme.colors.purplePrimary}, ${theme.colors.bluePrimary})` }}
            >
              {generating ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  生成中...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  生成大纲
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectOutlinePage;
